// Mobility, falls and incidents section (step 2) for VisitReportPage.

import { useRef, type JSX } from "react";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";
import { SwitchToggle } from "./SwitchToggle.js";
import type { FormState, Action } from "../lib/visit-report-types.js";
import { SECTION, HEADING } from "../lib/visit-report-types.js";
import { useFocusOnApiField } from "../hooks/use-focus-on-api-field.js";

const SELECT_BLANK = { value: "", label: "— select —" };
const MOB = [
  SELECT_BLANK,
  { value: "independent", label: "Independent" },
  { value: "uses_stick", label: "Uses stick" },
  { value: "uses_walker", label: "Uses walker" },
  { value: "uses_wheelchair", label: "Uses wheelchair" },
  { value: "bedbound", label: "Bedbound" },
];
const YN     = [{ value: "no", label: "No" }, { value: "yes", label: "Yes" }];
const CHANGE = [
  { value: "same",     label: "Same as last visit" },
  { value: "improved", label: "Improved" },
  { value: "worsened", label: "Worsened" },
];
const INJURY_DESC = [
  SELECT_BLANK,
  { value: "no_injury",          label: "No injury" },
  { value: "bruising_only",      label: "Bruising only" },
  { value: "laceration",         label: "Laceration" },
  { value: "suspected_fracture", label: "Suspected fracture" },
  { value: "head_injury",        label: "Head injury" },
];
const ESC_SEV = [
  SELECT_BLANK,
  { value: "low",      label: "Low" },
  { value: "medium",   label: "Medium" },
  { value: "high",     label: "High" },
  { value: "critical", label: "Critical" },
];
const HOME_CONCERN = [
  SELECT_BLANK,
  { value: "loose_rugs",       label: "Loose rugs" },
  { value: "poor_lighting",    label: "Poor lighting" },
  { value: "clutter",          label: "Clutter" },
  { value: "unsafe_stairs",    label: "Unsafe stairs" },
  { value: "no_grab_rails",    label: "No grab rails" },
  { value: "structural_hazard",label: "Structural hazard" },
  { value: "other",            label: "Other" },
];

// Q9 S5 — bus is reserved for free-text fields (currently
// fall_injury_description is a select, so MobilitySection has no blur-
// triggering inputs); accept the prop so the wizard's StepBody type
// stays uniform.
import type { SectionAutoSaveBus } from "./VisitReportWizard.js";
import { fieldErrorMessage, type SectionFieldErrors } from "../lib/visit-report-section-validators.js";

interface Props {
  f: FormState; dispatch: (a: Action) => void;
  isAtWanderingRisk: boolean; bus?: SectionAutoSaveBus;
  fieldErrors?: SectionFieldErrors;  // Q9 S6
  /** Q9 S7 — when set, the named field is focused after this section renders. */
  focusFieldName?: string | null;
}

export function MobilitySection({ f, dispatch, isAtWanderingRisk, bus: _bus, fieldErrors, focusFieldName }: Props): JSX.Element {
  const fn  = (field: Action["field"]) => (v: string) => dispatch({ field, value: v } as Action);
  const tog = (field: "medication_error_observed" | "wandering_behaviour_reported" | "environment_safety_concern") =>
    (v: boolean) => dispatch({ field, value: v });
  const err = (api: string): string | undefined => fieldErrorMessage(fieldErrors, api);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusOnApiField(containerRef, focusFieldName);

  return (
    <div ref={containerRef} className={SECTION}>
      <p className={HEADING}>2 — Mobility &amp; Falls</p>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Mobility status" value={f.mobility} onChange={fn("mobility")} options={MOB} error={err("mobility_status")} />
        <SelectField label="vs. last visit" value={f.mobility_vs_last} onChange={fn("mobility_vs_last")} options={CHANGE} error={err("mobility_vs_last_visit")} />
        <SelectField label="Fall during this visit?" value={f.fall_visit} onChange={fn("fall_visit")} options={YN} error={err("fall_during_visit")} />
        <SelectField label="Fall reported since last visit?" value={f.fall_since} onChange={fn("fall_since")} options={YN} error={err("fall_since_last_visit_reported")} />
        {f.fall_since === "yes" && (
          <FormField label="Number of falls since last visit" type="number" value={f.fall_count_since_last_visit}
            onChange={fn("fall_count_since_last_visit")} min={0} max={10} error={err("fall_count_since_last_visit")} required />
        )}
        {(f.fall_visit === "yes" || f.fall_since === "yes") && (
          <SelectField label="Injury sustained?" value={f.fall_injury_sustained} onChange={fn("fall_injury_sustained")} options={YN} error={err("fall_injury_sustained")} />
        )}
        {f.fall_injury_sustained === "yes" && (
          <SelectField label="Injury description" value={f.fall_injury_description} onChange={fn("fall_injury_description")}
            options={INJURY_DESC} error={err("fall_injury_description")} required />
        )}
        <SelectField label="Near miss incident?" value={f.near_miss} onChange={fn("near_miss")} options={YN} error={err("near_miss_reported")} />
        <SelectField label="Patient safety concern?" value={f.patient_safety_concern} onChange={fn("patient_safety_concern")} options={YN} error={err("patient_expressed_safety_concern")} />
      </div>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mt-4 mb-2">Incidents &amp; Safety</p>
      <div className="space-y-2">
        <SwitchToggle label="Medication error observed" checked={f.medication_error_observed}
          onChange={tog("medication_error_observed")} />
        {isAtWanderingRisk && (
          <SwitchToggle label="Wandering behaviour observed" checked={f.wandering_behaviour_reported}
            onChange={(v) => dispatch({ field: "wandering_behaviour_reported", value: v })} />
        )}
        <SwitchToggle label="Home environment safety concern" checked={f.environment_safety_concern}
          onChange={tog("environment_safety_concern")} />
      </div>
      {f.environment_safety_concern && (
        <div className="mt-2">
          <SelectField label="Concern type" value={f.home_condition_concern_type}
            onChange={fn("home_condition_concern_type")} options={HOME_CONCERN} error={err("home_condition_concern_type")} required />
        </div>
      )}
      {f.escalation && (
        <div className="mt-3">
          <SelectField label="Escalation severity" value={f.escalation_severity}
            onChange={fn("escalation_severity")} options={ESC_SEV} error={err("escalation_severity")} required />
        </div>
      )}
    </div>
  );
}
